import { useCallback, useEffect, useState } from "react";
import { FileUp, FolderCheck, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { LodgeSubmittalSheet } from "@/components/submittals/LodgeSubmittalSheet";
import { RevisionBlock } from "@/components/submittals/SubmittalDetailDialog";
import { SUBMITTAL_STATUS_META } from "@/components/submittals/submittalMeta";

/**
 * Phase 136 — contractor portal: lodge shop drawings / product data / samples / SWMS / certificates for the builder's review,
 * see every outcome and resubmit when asked. Files open in a new tab with the signed-in token.
 */
export const ContractorSubmittalsCard = ({ readOnly = false }) => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [resubmitFor, setResubmitFor] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => {
    api.get("/contractor/submittals").then((r) => { setData(r.data); setFailed(false); }).catch(() => setFailed(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (failed) return null;
  if (!data) return <Skeleton className="h-28 w-full" data-testid="contractor-submittals-loading" />;

  const rows = data.rows || [];
  const active = rows.filter((r) => ["pending_review", "revise_resubmit"].includes(r.status));
  const shown = showAll ? rows : active.length ? active : rows.slice(0, 3);
  const canLodge = (data.jobs || []).length > 0 && !readOnly; // Phase 164 — view-only trade logins cannot lodge

  return (
    <Card className={`shadow-sm ${data.needs_action ? "border-l-2 border-l-amber-500" : ""}`} data-testid="contractor-submittals-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-sm font-semibold">
          <FolderCheck className="h-4 w-4 text-primary" /> Submittals
          {data.pending > 0 && <Badge variant="outline" className={SUBMITTAL_STATUS_META.pending_review.cls} data-testid="contractor-submittals-pending-count">{data.pending} under review</Badge>}
          {data.needs_action > 0 && <Badge variant="outline" className={SUBMITTAL_STATUS_META.revise_resubmit.cls} data-testid="contractor-submittals-action-count">{data.needs_action} to resubmit</Badge>}
          {readOnly ? (
            <span className="ml-auto text-xs font-normal text-muted-foreground" data-testid="contractor-submittals-view-only">View only</span>
          ) : (
            <Button size="sm" className="ml-auto h-8" onClick={() => { setResubmitFor(null); setSheetOpen(true); }} disabled={!canLodge} title={canLodge ? "" : "You are not assigned to any job yet"} data-testid="contractor-submittals-new-btn">
              <FileUp className="mr-1.5 h-3.5 w-3.5" /> Lodge submittal
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {rows.length === 0 ? (
          <p className="py-3 text-sm text-muted-foreground" data-testid="contractor-submittals-empty">
            Nothing lodged yet. Send shop drawings, product data, samples, SWMS or certificates here for the builder to review before the work proceeds.
          </p>
        ) : (
          <ul className="divide-y divide-border" data-testid="contractor-submittals-list">
            {shown.map((s) => {
              const sm = SUBMITTAL_STATUS_META[s.status] || SUBMITTAL_STATUS_META.pending_review;
              const expanded = openId === s.id;
              const revs = s.revisions || [];
              const current = revs[revs.length - 1];
              return (
                <li key={s.id} className="py-3" data-testid={`contractor-submittal-${s.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setOpenId(expanded ? null : s.id)} data-testid={`contractor-submittal-toggle-${s.id}`}>
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">{s.number} · {s.job_name}</p>
                      <p className="mt-0.5 truncate text-sm font-medium">{s.title}</p>
                      <p className="text-xs text-muted-foreground">{s.type_label} · revision {s.current_revision} · lodged {fmtDate(s.created_at)}</p>
                    </button>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={sm.cls} data-testid={`contractor-submittal-status-${s.id}`}>{sm.label}</Badge>
                      {s.status !== "rejected" && !readOnly && (
                        <Button size="sm" variant={s.status === "revise_resubmit" ? "default" : "outline"} className="h-8" onClick={() => { setResubmitFor(s); setSheetOpen(true); }} data-testid={`contractor-submittal-resubmit-${s.id}`}>
                          <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> {s.status === "revise_resubmit" ? "Resubmit" : "Add revision"}
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setOpenId(expanded ? null : s.id)} aria-label={expanded ? "Collapse" : "Expand"}>
                        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  {!expanded && s.last_review?.comments && s.status === "revise_resubmit" && (
                    <p className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm" data-testid={`contractor-submittal-comments-${s.id}`}>
                      <span className="font-semibold text-amber-300">Builder asked:</span> {s.last_review.comments}
                    </p>
                  )}
                  {expanded && (
                    <div className="mt-3 space-y-2" data-testid={`contractor-submittal-revisions-${s.id}`}>
                      {s.description && <p className="whitespace-pre-wrap text-sm text-muted-foreground">{s.description}</p>}
                      {[...revs].reverse().map((rev) => <RevisionBlock key={rev.rev} sub={s} rev={rev} isCurrent={rev.rev === current?.rev} testPrefix={`contractor-rev-${s.id}`} />)}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {rows.length > shown.length || (showAll && rows.length > 3) ? (
          <button type="button" className="mt-2 text-xs text-muted-foreground underline-offset-2 hover:underline" onClick={() => setShowAll((v) => !v)} data-testid="contractor-submittals-toggle-all">
            {showAll ? "Show only active" : `Show all ${rows.length}`}
          </button>
        ) : null}
      </CardContent>

      <LodgeSubmittalSheet
        open={sheetOpen}
        onOpenChange={(v) => { setSheetOpen(v); if (!v) setResubmitFor(null); }}
        mode="contractor"
        jobs={data.jobs || []}
        resubmitFor={resubmitFor}
        onSaved={load}
      />
    </Card>
  );
};
